import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SessionInfoDTO } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { syncThemeFromAccount } from "@/lib/theme";

interface AuthContextValue {
  session: SessionInfoDTO | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  // motiv drží účet: po přihlášení (i na cizím stroji) přebije to,
  // co si prohlížeč pamatoval z minula
  const preference = query.data?.user.themePreference;
  useEffect(() => {
    if (preference) syncThemeFromAccount(preference);
  }, [preference]);

  const value: AuthContextValue = {
    session: query.data ?? null,
    loading: query.isLoading,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    logout: async () => {
      await api.logout();
      queryClient.setQueryData(["auth", "me"], null);
      queryClient.clear();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth mimo AuthProvider");
  return ctx;
}
